'use client'

import { motion } from 'motion/react'
import Link from 'next/link'
import { ArrowRight, Heart, Users, School } from 'lucide-react'

export function AboutPhilanthropy() {
    return (
        <section className="py-24 bg-white text-primary">
            <div className="container px-4 mx-auto">
                {/* Intro Text - Centered */}
                <div className="max-w-3xl mx-auto text-center mb-16 space-y-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="space-y-4"
                    >
                        <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
                            Giving Back
                        </span>
                        <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
                            Empowering <br /> <span className="italic text-primary/50">Communities</span>
                        </h2>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-xl text-muted-foreground leading-relaxed"
                    >
                        Our success is measured not just by the buildings we create, but by the lives we touch. Through the Nova Foundation, we are dedicated to fostering education, healthcare, and social welfare.
                    </motion.p>
                </div>

                {/* Content Layout */}
                <div className="grid lg:grid-cols-12 gap-12 items-start">

                    {/* Left: Stats & Initiatives */}
                    <div className="lg:col-span-4 space-y-12 pt-8">
                        <div className="space-y-8">
                            {[
                                { icon: School, label: "Education", value: "50+ Schools Supported" },
                                { icon: Heart, label: "Healthcare", value: "10k+ Lives Impacted" },
                                { icon: Users, label: "Community", value: "20+ Local Initiatives" },
                            ].map((stat, i) => (
                                <motion.div
                                    key={stat.label}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.3 + (i * 0.1) }}
                                    className="flex items-center gap-6 p-6 rounded-xl bg-muted/20 hover:bg-muted/40 transition-colors"
                                >
                                    <div className="w-12 h-12 rounded-full bg-[#C6A87C]/10 flex items-center justify-center shrink-0">
                                        <stat.icon className="w-6 h-6 text-[#C6A87C]" />
                                    </div>
                                    <div>
                                        <div className="text-2xl font-display text-primary">{stat.value}</div>
                                        <div className="text-muted-foreground uppercase text-xs tracking-wider">{stat.label}</div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <Link href="/contact" className="inline-flex items-center gap-2 text-primary font-medium hover:text-[#C6A87C] transition-colors group">
                            Partner with us for a cause <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>

                    {/* Right: Immersive Image */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-8 relative aspect-video rounded-2xl overflow-hidden shadow-2xl group"
                    >
                        <img
                            src="/images/about/philanthropy.png"
                            alt="Nova Realty Philanthropy"
                            className="object-cover w-full h-full transform transition-transform duration-1000 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" />

                        <div className="absolute bottom-0 left-0 p-8 md:p-12 text-white">
                            <blockquote className="text-xl md:text-2xl font-serif italic max-w-2xl leading-relaxed mb-6">
                                "The true foundation of any great society is the compassion we show to one another."
                            </blockquote>
                            <div className="flex items-center gap-4">
                                <div className="h-[1px] w-12 bg-[#C6A87C]" />
                                <span className="uppercase tracking-widest text-sm font-medium"> The Nova Foundation</span>
                            </div>
                        </div>
                    </motion.div>

                </div>
            </div>
        </section>
    )
}
